import { LocationStrategy } from '@angular/common';
import { Component, inject, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { IonBadge, IonButton, IonIcon, IonPopover, IonSpinner } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { checkmarkOutline, notificationsOutline, openOutline } from 'ionicons/icons';

import { NotificationService } from '../../core/notifications/notification.service';
import { internalRoute } from './notification-link';

/**
 * The bell in the top bar: how many announcements this person has not read, and the cards behind it.
 *
 * <p>Ported from the Blazor `TopMenu`'s bell, which was retired with `Core.Notifications`. Two
 * things about the original are deliberately kept — the count on the icon, and the coloured card
 * header, which is the only thing distinguishing "the office is closed tomorrow" from "the system is
 * down". Two are not: the acknowledge button no longer sits inside every card's body regardless of
 * whether the notice could be dismissed, and a card that cannot be dismissed now says so instead of
 * offering a button that fails.</p>
 *
 * <p>The message is authored HTML, so it is rendered with `[innerHTML]`. Angular sanitises it — a
 * `<script>` in a notification body is stripped rather than run — which the Blazor original, using
 * `MarkupString`, did not do.</p>
 */
@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [IonButton, IonIcon, IonBadge, IonPopover, IonSpinner],
  styles: `
    /* Sits on the icon rather than beside it, as the Blazor fa-layers-counter did. */
    .bell-badge {
      position: absolute;
      top: 2px;
      right: 0;
      transform: translate(25%, -25%);
      font-size: 0.65rem;
    }

    /* THE PANEL'S FRAME — set here rather than fought inside the content.
     *
     * ion-popover ships --width: 250px in md mode (200px in ios) from its own shadow stylesheet,
     * and .popover-content is overflow: auto. So ANY fixed-width child wider than 250px does not
     * widen the panel — it makes the panel scroll SIDEWAYS. This container was w-80 (320px), which
     * is why the bell opened with a horizontal scrollbar even when the only thing in it was the
     * words "Nothing new." (Sprints card 132: 250/320 is the three-quarter thumb in that screenshot).
     *
     * The rule reaches the element because <ion-popover> is in this component's template, so it
     * carries the encapsulation attribute; custom properties then inherit into the shadow root,
     * which is Ionic's documented customisation route. This is NOT the ion-select size: "cover"
     * trap written up in styles.scss — that one writes an INLINE --width and only applies to selects.
     *
     * So: set the frame, and leave the content fluid. Do not put a width utility back on the
     * container. The min() is the phone case, where a 352px panel on a 360px screen would otherwise
     * press against both edges. --max-height carries the cap the container's own max-h-96 used to,
     * which puts both caps on one element and leaves .popover-content as the SINGLE scroller
     * rather than one nested inside another. */
    ion-popover {
      --width: min(22rem, calc(100vw - 2rem));
      --max-height: min(24rem, 70vh);
    }

    /* A notification's message is authored HTML, so the frame above only holds if the message cannot
     * out-size it on its own — otherwise the sideways scrollbar comes back on the same element for a
     * different reason, the first time somebody pastes a wide table or a long URL into a notice.
     *
     * The keyword "anywhere" rather than "break-word" is load-bearing: only "anywhere" shrinks an
     * element's MIN-CONTENT width, which is what lets a table's cells give way to the max-width
     * below instead of holding it open. It is inherited, so the container covers the whole subtree.
     *
     * img and table are not inherited, and [innerHTML] content arrives WITHOUT the _ngcontent
     * attributes emulated encapsulation matches on — the same trap .mention is written up under in
     * styles.scss. Hence ::ng-deep, scoped under .notification-body so it cannot escape this
     * component. (normalize.css already caps img width globally; height: auto is the part that is
     * ours, so an authored img with width=900 height=600 scales instead of distorting.) */
    .notification-body {
      overflow-wrap: anywhere;
    }

    .notification-body ::ng-deep img {
      max-width: 100%;
      height: auto;
    }

    .notification-body ::ng-deep table {
      max-width: 100%;
    }

    .notification-body ::ng-deep pre {
      white-space: pre-wrap;
    }
  `,
  template: `
    <ion-button id="notification-bell" [attr.aria-label]="ariaLabel()" (click)="onOpen()">
      <span class="relative flex items-center">
        <ion-icon slot="icon-only" name="notifications-outline"></ion-icon>
        @if (notifications.unreadCount() > 0) {
          <ion-badge color="danger" class="bell-badge">{{ notifications.unreadCount() }}</ion-badge>
        }
      </span>
    </ion-button>

    <ion-popover
      trigger="notification-bell"
      [dismissOnSelect]="false"
      side="bottom"
      alignment="end"
    >
      <ng-template>
        <!-- No width and no scroller of its own: both live on the ion-popover above. -->
        <div class="p-2">
          @if (loading()) {
            <div class="flex justify-center p-6"><ion-spinner></ion-spinner></div>
          } @else if (notifications.unavailable()) {
            <p class="p-4 text-center text-sm text-gray-500">
              Notifications could not be loaded just now.
            </p>
          } @else if (notifications.items().length === 0) {
            <p class="p-4 text-center text-sm text-gray-500">Nothing new.</p>
          } @else {
            @for (item of notifications.items(); track item.id) {
              <article class="mb-2 overflow-hidden rounded border">
                <header
                  class="px-3 py-2 text-sm font-semibold"
                  [style.background]="item.categoryColor || '#f3f4f6'"
                >
                  {{ item.name }}
                </header>

                <!-- Authored HTML. Angular sanitises it on the way in; .notification-body is what
                     keeps it from out-sizing the panel (see the styles above). -->
                <div class="notification-body px-3 py-2 text-sm" [innerHTML]="item.message"></div>

                @if (item.linkUrl) {
                  <div class="px-3 pb-1">
                    <ion-button size="small" fill="outline" (click)="open(item.linkUrl!)">
                      <ion-icon slot="start" name="open-outline"></ion-icon>
                      Open
                    </ion-button>
                  </div>
                }

                <footer class="flex items-center justify-between px-3 pb-2">
                  <span class="text-xs text-gray-500">
                    @if (item.categoryName) {
                      {{ item.categoryName }}
                    }
                  </span>

                  @if (item.canAcknowledge) {
                    <ion-button size="small" fill="clear" (click)="dismiss(item.id)">
                      <ion-icon slot="start" name="checkmark-outline"></ion-icon>
                      Dismiss
                    </ion-button>
                  } @else {
                    <span class="text-xs text-gray-400 italic">Stays until it expires</span>
                  }
                </footer>
              </article>
            }
          }
        </div>
      </ng-template>
    </ion-popover>
  `,
})
export class NotificationBellComponent {
  protected readonly notifications = inject(NotificationService);
  protected readonly loading = signal(false);

  private readonly router = inject(Router);
  private readonly locationStrategy = inject(LocationStrategy);

  /** The panel itself, so following a link can close it. It is opened by `trigger`, not by code. */
  private readonly popover = viewChild(IonPopover);

  constructor() {
    addIcons({ notificationsOutline, checkmarkOutline, openOutline });
    this.notifications.start();
  }

  /** Re-read on open: a poll is minutes-coarse, and the moment somebody looks is worth being right. */
  protected async onOpen(): Promise<void> {
    this.loading.set(true);
    await this.notifications.refresh();
    this.loading.set(false);
  }

  protected async dismiss(id: number): Promise<void> {
    await this.notifications.acknowledge(id);
  }

  /**
   * Follow a notification's link — the whole of Sprints card 146's "bring me to the board where I am
   * mentioned".
   *
   * <p><b>Routed rather than navigated away to, where it is this app's own url.</b> The link is
   * absolute because the same string goes in the email, and assigning it to `location` would reload
   * the SPA to land two feet from where it started — every signal rebuilt, the SignalR connection
   * dropped and remade, and about two seconds of white screen. `internalRoute` does the conversion
   * and returns null for anything that is not ours, which is then followed as an ordinary link.</p>
   *
   * <p><b>The panel is closed first.</b> It is declared with `[dismissOnSelect]="false"`, because a
   * Dismiss button inside it must not shut the whole thing — but opening a card and leaving the
   * notifications hanging over it is not the same case.</p>
   */
  protected async open(url: string): Promise<void> {
    await this.popover()?.dismiss();

    const route = internalRoute(url, window.location.origin, this.locationStrategy.getBaseHref());

    if (route) {
      await this.router.navigateByUrl(route);
    } else {
      window.location.assign(url);
    }
  }

  protected ariaLabel(): string {
    const count = this.notifications.unreadCount();
    return count === 0 ? 'Notifications' : `Notifications (${count} unread)`;
  }
}
